import { ref } from 'vue'
import { defineStore } from 'pinia'
import * as ApiService from '@/core/services/ApiService'
import { SESSION_PORTAL_CONFIG_RESET_PENDING } from '@/constants/portalConfigSession'
import { useProfileStore, getFacilityKey, getPatientKey } from '@/stores/profile'
import type { ITaskJSONFormQueryData } from '@/types/api'
import type { CaseHistoryTaskAction, FvTaskAction, PortalConfigMeta } from '@/types/task-actions'
import type { WizardStep } from '@/utils/wizardFlow'
import { logError } from '@/utils/safeErrorLog'

interface CaseHistory {
  id: number
  problems: unknown[]
  oats: unknown[]
  complaintFormValues: unknown | null
  autoAccident: unknown[]
  aaFormValues: unknown | null
}

interface Access {
  isAccessOnly: boolean
  accessOnlyRoute: string
}

interface TaskForm {
  facilityFormID: number
  action: string
  formName: string
  data: unknown
  formData?: ITaskJSONFormQueryData
}

type TaskTypePayload = { back: true; removePage?: string; page?: string } | string

const defaultCaseHistory: CaseHistory = {
  id: 0,
  problems: [],
  oats: [],
  complaintFormValues: null,
  autoAccident: [],
  aaFormValues: null
}

function getPatientId(): number {
  return useProfileStore().currentPatient?.patientID ?? 0
}

function getFacilityId(): number {
  return useProfileStore().currentPatient?.facilityID ?? 0
}

/**
 * The open portal task: case history, task action, wizard plan and forms.
 * Persisted whole under localStorage `portal-task` (zustand format, version 0), as in React.
 */
export const useTaskStore = defineStore(
  'task',
  () => {
    const previousTaskType = ref<string[]>([])
    const currentTaskType = ref('')
    const caseHistory = ref<CaseHistory>({ ...defaultCaseHistory })
    const officeName = ref('')
    const taskCode = ref('')
    const access = ref<Access>({ isAccessOnly: false, accessOnlyRoute: '/' })
    const taskInfo = ref<string | Record<string, unknown>>('')
    const taskItem = ref<unknown>(undefined)
    const urllinkInfo = ref<unknown>(undefined)
    const isLoading = ref(false)
    const forms = ref<TaskForm[]>([])
    const currentFormData = ref<unknown>(undefined)
    /** Loaded from GET GetCaseHistoryTaskAction when user opens an isHistory task. */
    const caseHistoryTaskAction = ref<CaseHistoryTaskAction | null>(null)
    /** Portal task id for UpdateTaskComplete after portal case-history flow. */
    const activePortalTaskId = ref<number | null>(null)
    /** True when user entered /complaint from dashboard isHistory task (portal config wire at end). */
    const portalCaseHistoryFlow = ref(false)
    /** Last GET GetFvTaskAction result when opening an FV task. */
    const fvTaskAction = ref<FvTaskAction | null>(null)
    /** Parsed metadata from the PortalConfig JSON wire template. */
    const portalConfigMeta = ref<PortalConfigMeta | null>(null)
    /** Computed wizard steps for the new-patient flow. */
    const wizardSteps = ref<WizardStep[]>([])

    function setCaseHistoryTaskAction(payload: CaseHistoryTaskAction | null) {
      caseHistoryTaskAction.value = payload
    }

    function setActivePortalTaskId(id: number | null) {
      activePortalTaskId.value = id
    }

    function setPortalCaseHistoryFlow(v: boolean) {
      portalCaseHistoryFlow.value = v
    }

    function setFvTaskAction(payload: FvTaskAction | null) {
      fvTaskAction.value = payload
    }

    function setPortalConfigMeta(meta: PortalConfigMeta | null) {
      portalConfigMeta.value = meta
    }

    function setWizardSteps(steps: WizardStep[]) {
      wizardSteps.value = steps
    }

    function setCurrentTaskType(payload: string | TaskTypePayload) {
      if (typeof payload === 'object' && payload !== null && 'back' in payload && payload.back) {
        previousTaskType.value = previousTaskType.value.filter((c) => c !== payload.removePage)
        currentTaskType.value = payload.page ?? ''
      } else {
        const nextPage =
          typeof payload === 'string' ? payload : ((payload as { page?: string }).page ?? '')
        previousTaskType.value = [...previousTaskType.value, currentTaskType.value]
        currentTaskType.value = nextPage
      }
    }

    function setCurrentCaseHistoryId(payload: number) {
      caseHistory.value = { ...caseHistory.value, id: payload }
    }

    function setTaskCurrentCaseHistoryProblems(payload: unknown[]) {
      caseHistory.value = { ...caseHistory.value, problems: payload }
    }

    function setTaskCurrentCaseHistoryOats(payload: unknown[]) {
      caseHistory.value = { ...caseHistory.value, oats: payload }
    }

    function setTaskCurrentCaseHistoryComplaintFormVal(payload: unknown) {
      caseHistory.value = { ...caseHistory.value, complaintFormValues: payload }
    }

    function setTaskCurrentCaseHistoryAutoAccident(payload: unknown[]) {
      caseHistory.value = { ...caseHistory.value, autoAccident: payload }
    }

    function setTaskCurrentCaseHistoryAAFormValues(payload: unknown) {
      caseHistory.value = { ...caseHistory.value, aaFormValues: payload }
    }

    function setTaskOfficeName(payload: string) {
      officeName.value = payload
    }

    function setTaskIsAccessOnly(payload: string | boolean) {
      access.value = { ...access.value, isAccessOnly: !!payload }
    }

    function setTaskIsAccessOnlyRoute(payload: string) {
      access.value = { ...access.value, accessOnlyRoute: payload }
    }

    function setTaskInfo(payload: string | Record<string, unknown>) {
      taskInfo.value = payload
    }

    function setTaskItem(payload: unknown) {
      taskItem.value = payload
    }

    function setUrlLinkInfo(payload: unknown) {
      urllinkInfo.value = payload
    }

    function setTaskCode(code: string) {
      taskCode.value = code
    }

    /** Clear persisted case history when switching patients (id must not leak across patients). */
    function resetCaseHistory() {
      try {
        sessionStorage.removeItem(SESSION_PORTAL_CONFIG_RESET_PENDING)
      } catch {
        /* ignore */
      }
      caseHistory.value = { ...defaultCaseHistory }
      caseHistoryTaskAction.value = null
      activePortalTaskId.value = null
      portalCaseHistoryFlow.value = false
      fvTaskAction.value = null
      portalConfigMeta.value = null
      wizardSteps.value = []
    }

    async function fetchTaskForms(
      _facilityId: number | string = getFacilityId(),
      _patientId: number | string = getPatientId(),
      facilityKey: string = getFacilityKey()
    ): Promise<TaskForm[] | null> {
      isLoading.value = true
      try {
        const response = await ApiService.post('/Portal/GetForms', {
          facilityKey: facilityKey || getFacilityKey(),
          patientKey: getPatientKey()
        })
        const data = response.data as TaskForm[]
        forms.value = data
        return data
      } catch (error) {
        logError('Error task store fetchTaskForms:', error)
        return null
      } finally {
        isLoading.value = false
      }
    }

    function clearForms() {
      forms.value = []
    }

    async function fetchTaskFormData(
      facilityFormID: string | number,
      patientID: string | number = getPatientId()
    ): Promise<ITaskJSONFormQueryData | null> {
      isLoading.value = true
      try {
        const response = await ApiService.post('/Form/FacilityFormJson', {
          facilityFormID,
          patientID
        })
        const data = response.data as ITaskJSONFormQueryData
        currentFormData.value = data
        return data
      } catch (error) {
        logError('Error task store fetchTaskFormData:', error)
        return null
      } finally {
        isLoading.value = false
      }
    }

    return {
      previousTaskType,
      currentTaskType,
      caseHistory,
      officeName,
      taskCode,
      access,
      taskInfo,
      taskItem,
      urllinkInfo,
      isLoading,
      forms,
      currentFormData,
      caseHistoryTaskAction,
      activePortalTaskId,
      portalCaseHistoryFlow,
      fvTaskAction,
      portalConfigMeta,
      wizardSteps,
      setCaseHistoryTaskAction,
      setActivePortalTaskId,
      setPortalCaseHistoryFlow,
      setFvTaskAction,
      setPortalConfigMeta,
      setWizardSteps,
      setCurrentTaskType,
      setCurrentCaseHistoryId,
      setTaskCurrentCaseHistoryProblems,
      setTaskCurrentCaseHistoryOats,
      setTaskCurrentCaseHistoryComplaintFormVal,
      setTaskCurrentCaseHistoryAutoAccident,
      setTaskCurrentCaseHistoryAAFormValues,
      setTaskOfficeName,
      setTaskIsAccessOnly,
      setTaskIsAccessOnlyRoute,
      setTaskInfo,
      setTaskItem,
      setUrlLinkInfo,
      setTaskCode,
      resetCaseHistory,
      fetchTaskForms,
      fetchTaskFormData,
      clearForms
    }
  },
  { zustandPersist: { key: 'portal-task' } }
)
